
import React from 'react';
import { motion } from 'framer-motion';
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Zap, DollarSign } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/use-theme";

interface DeliveryPreferenceSelectorProps {
  deliveryPreference: 'Cheapest' | 'Fastest';
  onUpdate: (preference: 'Cheapest' | 'Fastest') => void;
}

const DeliveryPreferenceSelector: React.FC<DeliveryPreferenceSelectorProps> = ({
  deliveryPreference,
  onUpdate
}) => {
  const { primaryColor } = useTheme();

  const options = [
    {
      value: 'Cheapest' as const,
      label: 'Most Affordable',
      icon: DollarSign,
      description: 'Best price available',
      color: 'from-green-500 to-green-600'
    },
    {
      value: 'Fastest' as const,
      label: 'Fastest Delivery',
      icon: Zap,
      description: 'Priority shipping',
      color: 'from-blue-500 to-blue-600'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      <Label>Delivery Preference</Label>
      
      <ToggleGroup 
        type="single" 
        value={deliveryPreference} 
        onValueChange={(value) => value && onUpdate(value as 'Cheapest' | 'Fastest')}
        className="grid grid-cols-2 gap-3 w-full"
      >
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = deliveryPreference === option.value;
          
          return (
            <motion.div key={option.value} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <ToggleGroupItem
                value={option.value}
                variant="outline"
                className={cn(
                  "w-full h-auto p-4 flex flex-col items-center space-y-2 relative overflow-hidden",
                  "border-2 transition-all duration-200",
                  isSelected && "border-primary bg-primary/5"
                )}
                style={isSelected ? {
                  borderColor: primaryColor,
                  backgroundColor: `${primaryColor}08`
                } : undefined}
              >
                {/* Background gradient */}
                <div className={cn(
                  "absolute inset-0 bg-gradient-to-br opacity-5",
                  option.color
                )} />
                
                {/* Content */}
                <div className="relative flex flex-col items-center space-y-2">
                  <div className={cn(
                    "p-2 rounded-lg bg-gradient-to-br text-white",
                    option.color
                  )}>
                    <Icon className="h-5 w-5" />
                  </div>
                  
                  <div className="text-center">
                    <div className={cn(
                      "font-medium text-sm",
                      isSelected ? "text-primary" : "text-foreground"
                    )}>{option.label}</div>
                    <div className="text-xs text-muted-foreground">{option.description}</div>
                  </div>
                </div>
              </ToggleGroupItem>
            </motion.div>
          );
        })}
      </ToggleGroup>
    </motion.div>
  );
};

export default DeliveryPreferenceSelector;
